import { Navigate, Outlet } from 'react-router-dom';

import { PageSkeleton } from '@/components/ui/page-skeleton';
import { useCurrentTenant } from '@/hooks/scoped';
import {
  useDeferLoading,
  useFlexHeight,
  useFullWidth,
} from '@/hooks/use-breadcrumbs';
import { cn } from '@/lib/utils';

export default function ManageLayout() {
  const fullWidth = useFullWidth();
  const flexHeight = useFlexHeight();
  const deferLoading = useDeferLoading();
  const {
    data: tenant,
    isLoading,
    error,
    isSuccess,
    isFetching,
  } = useCurrentTenant();

  // Routes that render their own skeleton (e.g. the dashboard) opt out of the
  // blocking spinner during the initial tenant fetch so they can paint their
  // skeleton immediately. We still gate on errors below.
  const shouldDeferToPageSkeleton = deferLoading && isLoading && !error;

  // Show a neutral page skeleton while fetching (initial load or refetch after a
  // cached error). Routes with their own skeleton opt out via `deferLoading`.
  if (!shouldDeferToPageSkeleton && (isLoading || (error && isFetching))) {
    return <PageSkeleton />;
  }

  // Check if tenant is invalid: null, empty slug, or all-zeros UUID
  const isInvalidTenant =
    !tenant?.slug || tenant.id === '00000000-0000-0000-0000-000000000000';

  // If query completed but tenant is invalid, or errored with no cached data.
  // We keep `error && !tenant` so a failed background refetch doesn't kick the
  // user to 403 when valid tenant data is still available from a prior fetch.
  if ((isSuccess && isInvalidTenant) || (error && !tenant)) {
    return <Navigate to="/403" replace />;
  }

  if (fullWidth) {
    return <Outlet />;
  }

  return (
    <div
      className={cn(
        'mr-auto w-auto max-w-screen-2xl px-5 pt-3 sm:px-4',
        flexHeight && 'flex min-h-0 w-full flex-1 flex-col'
      )}
    >
      <Outlet />
    </div>
  );
}
